import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { useApi, unwrap } from "@backcoach/ui";

/**
 * Sign-in by magic link. The link in the email lands on this page with the
 * token in the URL; the page holds it and spends it only when the person
 * presses Continue, because mail scanners (Microsoft Safe Links among them)
 * open links and would otherwise use the single-use token before the admin
 * could. Against a local mock API the page also offers the mock's fixed
 * link, so the console runs with no email service; that button never
 * appears against a real API.
 */
const MOCK = /^https?:\/\/localhost(:\d+)?$/.test((import.meta as unknown as { env: Record<string, string | undefined> }).env["VITE_API_URL"] ?? "http://localhost:8787");

export function SignInScreen({ onSignedIn }: { onSignedIn: () => void }) {
  const api = useApi();
  const [email, setEmail] = useState(MOCK ? "priya.sharma@northgate.example" : "");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingToken, setPendingToken] = useState<string | null>(() => new URLSearchParams(window.location.search).get("token"));

  // The emailed link: keep the token in memory and take it out of the URL,
  // so a reload or a shared screenshot does not carry it.
  useEffect(() => {
    if (pendingToken) window.history.replaceState(null, "", window.location.pathname);
  }, []);

  async function send(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      unwrap(await api.POST("/v1/auth/magic/send", { body: { email } }));
      setSent(true);
    } catch {
      setError("Could not send the email. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  }

  async function verify(token: string) {
    setError(null);
    setBusy(true);
    const r = await api.POST("/v1/auth/magic/verify", { body: { token } });
    setBusy(false);
    setPendingToken(null);
    if (r.response.ok) onSignedIn();
    else setError("That link did not work. It may have been used or expired. Ask for a new one below.");
  }

  if (pendingToken) {
    return (
      <div className="bc-app">
        <main className="bc-main bc-flow-main">
          <div className="join">
            <img className="join-logo" src="/brand/lockup-stack-ink.svg" alt="Back Coach" width="150" height="150" />
            <p className="join-kicker">Console</p>
            <h1 className="join-head join-head-sm">One more click</h1>
            <p className="join-lede">You followed the link from your email. Press Continue to sign in. The link works once, and only from this button.</p>
            <button className="join-cta" type="button" disabled={busy} onClick={() => void verify(pendingToken)}>
              Continue to sign in
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="bc-app">
      <main className="bc-main bc-flow-main">
        <form className="join" onSubmit={(e) => void send(e)}>
          <img className="join-logo" src="/brand/lockup-stack-ink.svg" alt="Back Coach" width="150" height="150" />
          <p className="join-kicker">Console</p>
          <h1 className="join-head join-head-sm">Sign in</h1>
          <p className="join-lede">{sent ? `Check your email. The link we sent to ${email} signs you in here, once, within fifteen minutes.` : "We will email you a link. No password."}</p>
          <label className="join-field">
            <span>Email</span>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
          {error && (
            <p className="join-error" role="alert">
              {error}
            </p>
          )}
          <button className="join-cta" type="submit" disabled={busy}>
            {sent ? "Send another link" : "Email me a link"}
          </button>
          {sent && MOCK && (
            <>
              <p className="join-small">Local mock API: there is no email, so use the mock's fixed link.</p>
              <button type="button" className="join-alt" onClick={() => void verify(`mock-magic:${email}`)}>
                Use the mock link
              </button>
            </>
          )}
        </form>
      </main>
    </div>
  );
}
